import clsx from 'clsx'
import Linked from 'shared-components/service/Linked'
import CommentHollow from 'shared-svg/CommentHollow.svg'
import ArrowRightBird from 'shared-svg/ArrowRightBird.svg'
import { BasicStyledComponent } from 'shared-definitions/types'
import { communityHref } from 'lib/community/model'
import type { CommunityPill } from 'lib/community/entry-pill'
import { publicConfig } from 'config'
import { useCommunityPill } from './useCommunityPill'
import styles from './CommunityEntry.module.css'

/** The Community entry's words — one copy for the header chips, the drawer row and the home card. */
export const COMMUNITY_ENTRY = {
  title: 'Community',
  subtitle: 'Owner reviews and discussions',
} as const

/**
 * The count pill beside a Community entry (`communityPill`): light blue with the posts of the last
 * 24 h (else the week), red with a member's unread count. Prints the number only — every caller
 * puts the words where they are read (title, aria-label, subtitle or `PillWords`).
 */
export const CommunityCountPill: React.VFC<BasicStyledComponent & { pill: CommunityPill }> = ({
  className,
  pill,
}) => (
  <span
    className={clsx(styles.countPill, pill.kind === 'member' && styles.countPillMember, className)}
    aria-hidden="true"
  >
    {pill.count}
  </span>
)

/** The pill's words for a screen reader, where nothing visible says them (the xl nav chip). */
export const PillWords: React.VFC<{ pill: CommunityPill }> = ({ pill }) => (
  <span className={styles.srOnly}>{`, ${pill.label}`}</span>
)

/** `pill` given (a test, a caller that already holds it) wins; else the page's live one. */
function usePillOr(given: CommunityPill | null | undefined): CommunityPill | null {
  const live = useCommunityPill()
  return given !== undefined ? given : live
}

interface CommunityEntryCardProps extends BasicStyledComponent {
  onClick?: () => void
  /** `outlined` (default): the drawer row, blue 1.5px outline (design H4A). `light`: the
   *  phone home card, pale 1px border at the search field's colour, 56px (design H5C). */
  tone?: 'outlined' | 'light'
  /** Override the live pill (tests). */
  pill?: CommunityPill | null
}

/**
 * The Community destination card: a bubble icon tile, "Community" + the count pill, one line on
 * what is inside (the pill's words when there is one), a chevron. ONE component for the drawer's
 * row under "Best daily deals" and the phone home's card above the hero (`CommunityStrip`), so
 * markup, text, pill and link can't drift; only the frame differs per surface (`tone`).
 */
export const CommunityEntryCard: React.VFC<CommunityEntryCardProps> = ({
  className,
  onClick,
  tone = 'outlined',
  pill: given,
}) => {
  const pill = usePillOr(given)
  return (
    <Linked
      href={communityHref(publicConfig('useTrailingSlash'))}
      className={clsx(styles.card, tone === 'light' && styles.light, className)}
      onClick={onClick}
    >
      <span className={styles.iconTile} aria-hidden="true">
        <CommentHollow className={styles.icon} />
      </span>
      <span className={styles.text}>
        <span className={styles.titleRow}>
          <span className={styles.title}>{COMMUNITY_ENTRY.title}</span>
          {pill && <CommunityCountPill pill={pill} />}
        </span>
        <span className={styles.subtitle}>{pill?.label ?? COMMUNITY_ENTRY.subtitle}</span>
      </span>
      <ArrowRightBird className={styles.arrow} />
    </Linked>
  )
}

interface CommunityHeaderChipProps extends BasicStyledComponent {
  /** Lit on any /community page. */
  active?: boolean
  /** Override the live pill (tests). */
  pill?: CommunityPill | null
}

/**
 * The header row's Community chip below xl (owner funnel C13, 2026-10-09) — phones and tablets
 * had no Community entry outside the drawer. The xl nav keeps its own "Community" chip.
 * Below sm the row has ~38px left beside the 163px logo, the burger and Login (360px), so the
 * chip is the drawer card's bubble tile alone, its name visually hidden, the count pill hanging on
 * its corner; from sm it spells "Community" + the pill inline. The server HTML is the plain chip for
 * every reader — the pill arrives after mount (`useCommunityPill`).
 */
export const CommunityHeaderChip: React.VFC<CommunityHeaderChipProps> = ({
  className,
  active = false,
  pill: given,
}) => {
  const pill = usePillOr(given)
  return (
    <Linked
      href={communityHref(publicConfig('useTrailingSlash'))}
      className={clsx(styles.headerChip, active && styles.headerChipActive, className)}
      aria-current={active ? 'page' : undefined}
      aria-label={pill ? `${COMMUNITY_ENTRY.title}, ${pill.short}` : undefined}
      title={pill?.label ?? COMMUNITY_ENTRY.subtitle}
    >
      <CommentHollow className={styles.headerChipIcon} aria-hidden="true" />
      <span className={styles.headerChipLabel}>{COMMUNITY_ENTRY.title}</span>
      {pill && <CommunityCountPill pill={pill} className={styles.headerChipPill} />}
    </Linked>
  )
}

/**
 * The phone home's Community card (design H5C): rendered by `HomeTemplate` between the
 * affiliate disclaimer and the hero, phones only (from md: the drawer row, and the header
 * chip at xl).
 * The server HTML is the plain card — nothing reader-specific — so the home's ISR HTML stays
 * shared-cacheable; the count pill and its subtitle arrive after mount.
 */
export const CommunityStrip: React.VFC = () => (
  <div className={styles.strip}>
    <CommunityEntryCard tone="light" />
  </div>
)
